import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Profile } from '../job-service/service-models/profile';
import { Answer } from './service-models/Answer';
import { Application } from './service-models/Application';
import { Education } from './service-models/education';
import { Experience } from './service-models/experience';
import { PartialSubmission } from './service-models/PartialSubmission';
import { Reference } from './service-models/reference';
import { ResumeParsingStatusModel } from './service-models/ResumeParsingStatusModel';

@Injectable({
  providedIn: 'root'
})
export class ApplicantService {
  apiGatewayUrl: string;

  constructor(private http: HttpClient) {
    this.apiGatewayUrl = environment().apiGatewayUrl;
  }

  createApplication(application: Application): Observable<HttpResponse<any>> {
    return this.http.post<any>(`${this.apiGatewayUrl}applications`, application, { observe: 'response' });
  }

  getNewApplicantId(jobId: number): Observable<{ applicantId: string }> {
    return this.http.get<{ applicantId: string }>(`${this.apiGatewayUrl}jobs/${jobId}/applicants/new-id`);
  }

  uploadCoverLetter(jobId: number, applicantId: string, coverLetter: File): any {
    const formData = new FormData();

    formData.append('coverLetter', coverLetter, coverLetter.name);
    formData.append('jobId', jobId.toString());
    formData.append('applicantId', applicantId.toString());
    
    return this.http.post<any>(`${this.apiGatewayUrl}applications/cover-letter`, formData);
  }

  deleteCoverLetter(jobId: number, applicantId: string, fileName: string) {
    return this.http.delete(
      `${this.apiGatewayUrl}applications/cover-letter/job/${jobId.toString()}/applicant/${applicantId}/file/${fileName}`);
  }
  
  uploadResume(jobId: number, applicantId: string, resume: File) {
    const formData = new FormData();

    formData.append('resume', resume, resume.name);
    formData.append('jobId', jobId.toString());
    formData.append('applicantId', applicantId.toString());
    
    return this.http.post<any>(`${this.apiGatewayUrl}applications/resume`, formData);
  }

  deleteResume(jobId: number, applicantId: string, fileName: string) {
    return this.http.delete(`${this.apiGatewayUrl}applications/resume/job/${jobId.toString()}/applicant/${applicantId}/file/${fileName}`);
  }

  uploadSurvey(elementId: number, applicationId: string, jobId: number, applicantId: string, answers: Array<Answer>, files: Array<File>) {
    const formData = new FormData();

    formData.append('jobId', jobId.toString());
    formData.append('applicantId', applicantId.toString());
    formData.append('answers', JSON.stringify(answers));
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i], files[i].name);
    }

    return this.http.post<any>(
      `${this.apiGatewayUrl}applications/${applicationId}/custom-applicant-elements/${elementId}/answers`,
      formData);
  }

  updateSurvey(elementId: number, applicationId: string, jobId: number, applicantId: string, answers: Array<Answer>, files: Array<File>) {
    const formData = new FormData();

    formData.append('jobId', jobId.toString());
    formData.append('applicantId', applicantId.toString());
    formData.append('answers', JSON.stringify(answers));
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i], files[i].name);
    }

    return this.http.put<any>(
      `${this.apiGatewayUrl}applications/${applicationId}/custom-applicant-elements/${elementId}/answers`,
      formData);
  }

  createProfile(applicationId: string, profile: Profile) {
    return this.http.post<any>(`${this.apiGatewayUrl}applications/${applicationId}/profile`, profile);
  }

  updateProfile(applicationId: string, profile: Profile) {
    return this.http.put<any>(`${this.apiGatewayUrl}applications/${applicationId}/profile`, profile);
  }

  updateEmploymentHistory(applicationId: string, experiences: Array<Experience>) {
    return this.http.put<any>(`${this.apiGatewayUrl}applications/${applicationId}/employment-history`, experiences);
  }

  updateReferences(applicationId: string, references: Array<Reference>) {
    return this.http.put<any>(`${this.apiGatewayUrl}applications/${applicationId}/references`, references);
  }

  getResumeParsingState(applicationId: number): Observable<ResumeParsingStatusModel> {
    return this.http.get<ResumeParsingStatusModel>(`${this.apiGatewayUrl}applications/${applicationId}/resume/parse-status`);
  }

  skipResumeParsing(applicationId: number) {
    return this.http.put<any>(`${this.apiGatewayUrl}applications/${applicationId}/skip-parsing`, null);
  }

  updateEducationHistory(applicationId: string, education: Array<Education>): Observable<any> {
    return this.http.put<any>(`${this.apiGatewayUrl}applications/${applicationId}/education-history`, education);
  }

  partiallySubmitApplication(emailAddress: string, route: string, applicantElement: string, jobId: number = 0) {
    const applicationId = +localStorage.getItem('applicationId');
    const partialSubmission = new PartialSubmission();
    partialSubmission.emailAddress = emailAddress;
    const completeApplicationPartRequestId = sessionStorage.getItem('completeApplicationPartRequestId');
    if (completeApplicationPartRequestId != null) {
      partialSubmission.completeApplicationPartRequestId = +completeApplicationPartRequestId;
    }
    partialSubmission.jobSiteRelativeUrl = route;
    partialSubmission.applicantElement = applicantElement;
    partialSubmission.jobId = jobId;
    return this.http.post<any>(`${this.apiGatewayUrl}applications/${applicationId}/partial-submissions`, partialSubmission);
  }

  submitApplication(applicationId: string, answers: Array<Answer>) {
    return this.http.post<any>(`${this.apiGatewayUrl}applications/${applicationId}/submissions`,
      { timestamp: new Date(), certificationAnswers: answers });
  }

  submitIterativeApplicationPart(applicationId: number, partId: number) {
    return this.http.put<any>(`${this.apiGatewayUrl}applications/${applicationId}/iterative-application-part/${partId}`,
      { timestamp: new Date() });
  }

  submitCompleteIterativeApplicationPartRequest(applicationId: string, answers: Array<Answer>, completeApplicationPartRequestId: number) {
    return this.http.put<any>(`${this.apiGatewayUrl}applications/${applicationId}/iterative-application-part/0`,
      { timestamp: new Date(), certificationAnswers: answers, completeApplicationPartRequestId });
  }
}
